import {
	BlogFont,
	BlogHomeCustomization,
	BlogPostCustomization,
	CustomPageType,
	FooterCustomization,
	HeaderCustomization,
	NavLink,
	TextAttributes,
} from '@ViewModels';

/**
 * Default blog font values used across all forms
 */
export const DEFAULT_FONT_VALUES: BlogFont = {
	fontName: '',
	fontUri: '',
};

export const MIN_FONT_SIZE_PX = 8;
export const MAX_FONT_SIZE_PX = 120;
export const DEFAULT_BLOG_HOME_TITLE_FONT_SIZE_PX = 40;
export const DEFAULT_BLOG_CARD_TITLE_FONT_SIZE_PX = 22;
export const DEFAULT_BLOG_LINK_FONT_SIZE_PX = 16;
export const DEFAULT_BLOG_POST_TITLE_FONT_SIZE_PX = 32;
export const DEFAULT_BLOG_BODY_FONT_SIZE_PX = 16;
export const DEFAULT_BLOG_BUTTON_FONT_SIZE_PX = 16;

/**
 * Default text attributes including font, color, and background
 */
export const DEFAULT_TEXT_ATTRIBUTES: TextAttributes = {
	font: { ...DEFAULT_FONT_VALUES },
	color: '#000000',
	backgroundColor: '#ffffff',
};

/**
 * Default navigation link structure for menu items.
 * Children are added on demand by the user, not pre-populated.
 */
export const DEFAULT_NAV_LINK_VALUES: NavLink = {
	label: '',
	destination: '',
};

/**
 * Default simple link structure without children
 */
export const DEFAULT_SIMPLE_LINK_VALUES = {
	label: '',
	destination: '',
};

/**
 * Default text values used in footer when content is empty
 */
export const DEFAULT_FOOTER_BACKEND_TEXT_VALUES = {
	footerText: 'Add your disclaimer or footer text here.',
	copyrightInfo: 'Your Company Name. All rights reserved.',
};

/**
 * Default form values for Header customization
 * These values match the API defaults in CustomPageStyleService.GetSampleHeader()
 */
export const DEFAULT_HEADER_FORM_VALUES: HeaderCustomization = {
	type: CustomPageType.Header,
	backgroundColor: '#FFFFFF',
	dropdown: {
		color: '#FFFFFF',
		backgroundColor: '#000000',
		font: {
			fontName: '',
			fontUri: '',
		},
	},
	hoverColor: '#599EED',
	logoOnlyHeader: false,
	overallFont: {
		fontName: '',
		fontUri: '',
	},
	textColor: '#000000',
	mainPageTitle: '',
	menuLinks: [],
	// Empty = "not customized"; untouched forms keep Levitate defaults.
	bodyFont: {
		fontName: '',
		fontUri: '',
	},
	primaryButton: {
		backgroundColor: '',
		color: '',
		hoverColor: '',
	},
};

// Levitate defaults shown in the Page Body color pickers before any customization.
export const DEFAULT_PRIMARY_BUTTON_DISPLAY_COLORS = {
	backgroundColor: '#0066FF',
	color: '#FFFFFF',
	hoverColor: '#00CCFF',
};

/**
 * Default form values for Footer customization
 * These values match the API defaults in CustomPageStyleService.GetSampleFooter()
 */
export const DEFAULT_FOOTER_FORM_VALUES: FooterCustomization = {
	type: CustomPageType.Footer,
	backgroundColor: '#E9EBEF',
	textColor: '#9198A4',
	linkColor: '#0066FF',
	text: {
		document: '',
		documentVersion: 0,
		source: '',
	},
	copyrightInfo: '',
	links: new Array(3).fill(DEFAULT_SIMPLE_LINK_VALUES),
};

/**
 * Default form values for Blog Home customization
 * These values match the API defaults in CustomPageStyleService.GetSampleBlogHome()
 */
export const DEFAULT_HOME_FORM_VALUES: BlogHomeCustomization = {
	pageBackgroundColor: '#FFFFFF',
	headerImage: null,
	font: {
		fontName: '',
		fontUri: '',
	},
	blogTitle: {
		font: { ...DEFAULT_FONT_VALUES },
		color: '#000000',
		backgroundColor: '',
		fontSizePx: DEFAULT_BLOG_HOME_TITLE_FONT_SIZE_PX,
		title: 'Blogs',
	},
	cardStyle: {
		titleStyle: {
			font: { ...DEFAULT_FONT_VALUES },
			color: '#000000',
			backgroundColor: '',
			fontSizePx: DEFAULT_BLOG_CARD_TITLE_FONT_SIZE_PX,
		},
		linkStyle: {
			font: { ...DEFAULT_FONT_VALUES },
			color: '#599EED',
			backgroundColor: '',
		},
		showAuthorMetaData: true,
	},
	hideNook: false,
	type: CustomPageType.BlogHome,
};

/**
 * Default form values for Blog Post customization
 * These values match the API defaults in CustomPageStyleService.GetSampleBlogPost()
 */
export const DEFAULT_POST_FORM_VALUES: BlogPostCustomization = {
	blogTitle: {
		font: { ...DEFAULT_FONT_VALUES },
		color: '#000000',
		backgroundColor: '',
	},
	blogHeadlines: {
		font: { ...DEFAULT_FONT_VALUES },
		color: '#000000',
		backgroundColor: '',
	},
	blogBody: {
		font: { ...DEFAULT_FONT_VALUES },
		color: '#000000',
		backgroundColor: '#FFFFFF',
		fontSizePx: DEFAULT_BLOG_BODY_FONT_SIZE_PX,
	},
	showMoreButton: {
		font: { ...DEFAULT_FONT_VALUES },
		color: '#FFFFFF',
		backgroundColor: '#599EED',
		hoverColor: '#035697',
	},
	type: CustomPageType.BlogPost,
};

/**
 * Minimum number of footer links
 */
export const MINIMUM_FOOTER_LINKS = 3;

/**
 * Checks if the provided footer text is the default backend text
 * @param text - The footer text to check
 * @returns true if the text matches the default footer text
 */
export const isDefaultFooterText = (text: string): boolean => {
	if (!text) {
		return false;
	}
	const trimmedText = text.trim();
	return (
		trimmedText === DEFAULT_FOOTER_BACKEND_TEXT_VALUES.footerText ||
		trimmedText === `<p>${DEFAULT_FOOTER_BACKEND_TEXT_VALUES.footerText}</p>` ||
		trimmedText.replace(/<[^>]*>/g, '').trim() === DEFAULT_FOOTER_BACKEND_TEXT_VALUES.footerText
	);
};
